import { useCustom } from "@refinedev/core";
import { AlertTriangle } from "lucide-react";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { Combo } from "@/components/Combo";
import { SpisanieFeed } from "@/components/SpisanieFeed";
import { query } from "@/lib/api";
import { errorMessage, spisanieWord } from "@/lib/format";
import type { SpisanieJournal } from "@/lib/providers";
import { ALL_CAUSES, causeName, causeOptions } from "@/lib/spisanie";
import { useSessionGuard } from "@/lib/useSessionGuard";

/** Сколько строк добавляет одно «показать ещё». Ровно страница API. */
const PAGE = 50;

/**
 * Потолок страницы на стороне сервиса (`repo/spisanie.py::PAGE_MAX`).
 *
 * Дальше него список не растёт, и это не ограничение, а ответ: у категории
 * «Маркетплейс» 296 219 строк, и двести первых — не начало их чтения, а
 * признак того, что вопрос задан не тот. Сужают его причиной, а не прокруткой.
 */
const LIMIT_MAX = 200;

/**
 * Раздел «СПИСАНИЕ» — что склад списал и ОТКУДА.
 *
 * Вопрос свой, не совпадающий с соседями: «Товары» считают модель, «Баркод»
 * знает один размер, «Серия» разбирает одну вещь, «Место» рассказывает про
 * точку склада. Здесь объект — сама ОПЕРАЦИЯ: когда, что и откуда сняли с
 * учёта.
 *
 * ОТБОР ПО УМОЛЧАНИЮ — «Не нашли» (решение владельца 11.09.26). Списаний
 * категории «Маркетплейс» 296 219, наших «Не нашли» — четыре: без отбора
 * четыре строки про пропажу утонули бы в отгрузках маркетплейсу навсегда.
 *
 * Отбор и потолок живут В АДРЕСЕ (`?category=`, `?n=`): ссылку на находку
 * отправляют сменщику, и открыться она обязана такой же.
 *
 * Строка ведёт в ТРИ карточки — баркода, серии и места, — и это весь смысл
 * раздела: «что мы списали» — начало разбора, а не его конец.
 */
export function SpisaniePage() {
  const [params, setParams] = useSearchParams();
  const categoryParam = params.get("category") ?? "";
  const limitParam = params.get("n") ?? "";
  // Место, которым сужен журнал. Приходит из карточки места — оттуда, где
  // полсотни её строк кончились.
  const placeParam = params.get("place") ?? "";

  const url = useMemo(
    () =>
      `/api/spisanie${query({
        category: categoryParam || undefined,
        n: limitParam || undefined,
        place: placeParam || undefined,
      })}`,
    [categoryParam, limitParam, placeParam],
  );

  const listQuery = useCustom<SpisanieJournal>({
    url,
    method: "get",
    queryOptions: {
      // ПРЕЖНИЕ СТРОКИ ОСТАЮТСЯ, пока едут новые: без этого список на время
      // запроса пустеет и страница схлопывается под рукой — та же мина, что
      // ловили в ленте раздела «Баркод» 09.09.26. Что строки уже не про этот
      // отбор, говорит «обновляю…» в заголовке.
      placeholderData: (prev) => prev,
    },
  });

  const loaded = listQuery.result.data;
  // `result.data` у Refine существует и ДО загрузки — пустым объектом.
  // Журналом считается только payload, в котором есть массив строк.
  const journal = loaded && Array.isArray(loaded.rows) ? loaded : null;
  const busy = listQuery.query.isFetching;
  const error = listQuery.query.error;
  useSessionGuard(error);

  /** Правит один ключ адреса, не трогая остальные. */
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Смена ПРИЧИНЫ возвращает список к первой странице: поднятый потолок
    // относился к прежнему отбору, и тащить его в новый значит просить две
    // сотни строк там, где хотели посмотреть первые.
    if (key !== "n") next.delete("n");
    setParams(next, { replace: true });
  };

  const categories = journal?.categories ?? [];
  // Что показано, говорит ОТВЕТ, а не адрес: отбор по умолчанию живёт на
  // сервисе, и пустой `?category=` означает не «все», а «Не нашли».
  const chosen = journal ? String(journal.category_id) : categoryParam;
  const limit = journal?.limit ?? PAGE;
  const rows = journal?.rows ?? [];

  return (
    <main
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-5"
      data-component="SpisaniePage"
      data-annot="списание"
    >
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <h1 className="text-[22px] font-semibold tracking-[-0.02em]">Списание</h1>
        <span className="text-[13px] text-fg-muted">
          {busy && !journal
            ? "загружаю…"
            : `${rows.length} ${spisanieWord(rows.length)}`}
          {busy && journal ? " · обновляю…" : ""}
          {journal?.has_more ? " · есть ещё" : ""}
        </span>
      </header>

      {/* Причина — ГЛАВНЫЙ отбор раздела, и выпадающим списком он сделан
          намеренно, в отличие от городов в соседних разделах: причин двадцать
          девять, и рядом кнопок они не помещаются вовсе. Число строк стоит
          правой колонкой — им и выбирают из равных. */}
      <div className="flex flex-wrap items-center gap-2" data-annot="причина">
        <Combo
          label="Причина"
          value={chosen}
          options={causeOptions(categories)}
          onChange={(value) => set("category", value)}
          placeholder="поиск причины"
          width={260}
        />
        {/* Сужение МЕСТОМ названо вслух и снимается одним щелчком: журнал,
            суженный коробом, внешне неотличим от полного, а пришли сюда с
            карточки этого короба. */}
        {journal?.place_id ? (
          <span
            data-annot="отбор места"
            className="flex items-center gap-1.5 text-[12.5px] text-fg-muted"
          >
            только место
            <Link
              to={`/place/${journal.place_id}`}
              className="tnum underline decoration-border underline-offset-2 hover:decoration-fg"
            >
              {journal.place_id}
            </Link>
            <button
              type="button"
              onClick={() => set("place", "")}
              title="Показать списания всего склада"
              className="rounded-[var(--radius-ui)] border border-border px-1.5 py-0.5 hover:bg-bg-hover"
            >
              снять
            </button>
          </span>
        ) : null}
        {chosen && chosen !== ALL_CAUSES ? (
          <span className="text-[12.5px] text-fg-muted">
            {/* Отбор назван вслух: список, суженный причиной, внешне
                неотличим от полного, и четыре строки читались бы как весь
                журнал склада. */}
            показаны только «{causeName(categories, chosen)}»
          </span>
        ) : null}
      </div>

      {error ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-6 text-center"
        >
          <AlertTriangle size={20} className="text-err" />
          <p className="text-[13.5px]">{errorMessage(error)}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
          {busy
            ? "Загружаю…"
            : chosen && chosen !== ALL_CAUSES
              ? `По причине «${causeName(categories, chosen)}» не списывали ни разу.`
              : "Списаний нет вовсе."}
        </div>
      ) : (
        <SpisanieFeed
          rows={rows}
          caption="Списания склада: что списали, откуда, когда и чьей рукой"
          withCard
          withPlace
          withCategory={chosen === ALL_CAUSES}
        />
      )}

      {journal?.has_more && limit < LIMIT_MAX ? (
        <button
          type="button"
          onClick={() => set("n", String(Math.min(limit + PAGE, LIMIT_MAX)))}
          disabled={busy}
          data-annot="ещё"
          className="h-8 w-fit rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-55"
        >
          {busy ? "Загружаю…" : `Ещё ${Math.min(PAGE, LIMIT_MAX - limit)}`}
        </button>
      ) : null}

      {journal?.has_more && limit >= LIMIT_MAX ? (
        <p className="text-[12.5px] text-fg-muted">
          {/* Молча упереться в потолок нельзя: список выглядел бы полным. */}
          Показаны первые {LIMIT_MAX} — дальше список не растёт. Сузьте причину.
        </p>
      ) : null}
    </main>
  );
}

